import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import '../lib/charts';
import { SERIES, moneyTick } from '../lib/charts';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { money, moneyCompact, int, pct, niceDate } from '../lib/format';
import { Card, KpiCard, Spinner, ErrorBox, Empty, DataTable, Delta } from '../components/ui';

// Same-store sales (YoY comps): selected window vs the same window a year
// ago, over the comp base — locations with sales in BOTH windows. New or
// closed locations are listed but excluded from the comp numbers.
export default function SameStoreSalesPage() {
  const { reportParams, startDate, endDate } = useFilters();
  const [align, setAlign] = useState('weekday');

  const { data, loading, error } = useReport('same-store-sales', { ...reportParams, align });

  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Spinner label="Comparing against last year…" />;

  const { locations, trend, priorWindow } = data;
  const comp = locations.filter(l => l.isComp);
  const nonComp = locations.length - comp.length;

  const sum = (rows, f) => rows.reduce((a, r) => a + (r[f] || 0), 0);
  const currNet = sum(comp, 'currNetSales');
  const prevNet = sum(comp, 'prevNetSales');
  const currOrders = sum(comp, 'currOrders');
  const prevOrders = sum(comp, 'prevOrders');
  const currAov = currOrders ? currNet / currOrders : 0;
  const prevAov = prevOrders ? prevNet / prevOrders : 0;
  const compPct = prevNet > 0 ? (currNet - prevNet) / prevNet : null;

  // Prior series aligned by day index (dates differ by a year)
  const n = Math.max(trend.current.length, trend.prior.length);
  const chartData = {
    labels: Array.from({ length: n }, (_, i) => trend.current[i] ? niceDate(trend.current[i].date) : ''),
    datasets: [
      {
        label: 'This year',
        data: Array.from({ length: n }, (_, i) => trend.current[i]?.netSales ?? null),
        borderColor: SERIES[0],
        backgroundColor: 'rgba(42,120,214,0.08)',
        borderWidth: 2,
        pointRadius: n > 45 ? 0 : 2.5,
        tension: 0.25,
      },
      {
        label: 'Last year',
        data: Array.from({ length: n }, (_, i) => trend.prior[i]?.netSales ?? null),
        borderColor: '#898781',
        borderDash: [5, 4],
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.25,
      },
    ],
  };

  const alignChips = [
    ['weekday', 'Same weekday (52 wks)'],
    ['calendar', 'Calendar date'],
  ];

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="grid kpi-grid">
        <KpiCard
          label="Comp Sales"
          value={compPct === null ? '—' : `${compPct > 0 ? '+' : ''}${pct(compPct)}`}
          curr={currNet} prev={prevNet}
          sub={`${moneyCompact(currNet)} vs ${moneyCompact(prevNet)} LY`}
        />
        <KpiCard
          label="Comp Orders"
          value={prevOrders ? `${currOrders >= prevOrders ? '+' : ''}${pct((currOrders - prevOrders) / prevOrders)}` : '—'}
          curr={currOrders} prev={prevOrders}
          sub={`${int(currOrders)} vs ${int(prevOrders)} LY`}
        />
        <KpiCard
          label="Comp Avg Order"
          value={money(currAov)}
          curr={currAov} prev={prevAov}
          sub={`${money(prevAov)} LY`}
        />
        <KpiCard
          label="Comp Base"
          value={`${comp.length} of ${locations.length}`}
          sub={nonComp > 0 ? `${nonComp} new/closed excluded` : 'all locations comparable'}
        />
      </div>

      <Card
        tour="sss-chart"
        title="Daily Net Sales — This Year vs Last Year"
        sub={`${niceDate(startDate)} – ${niceDate(endDate)} vs ${niceDate(priorWindow.start)} – ${niceDate(priorWindow.end)} · comp-base locations only`}
        actions={
          <div className="chip-row">
            {alignChips.map(([id, label]) => (
              <button key={id} className={`chip ${align === id ? 'active' : ''}`} onClick={() => setAlign(id)}>{label}</button>
            ))}
          </div>
        }
      >
        <div className="chart-box">
          {trend.current.length === 0 ? <Empty>No comp-base data for this window.</Empty> : (
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'bottom' },
                  tooltip: {
                    callbacks: {
                      title: items => {
                        const i = items[0]?.dataIndex;
                        const cur = trend.current[i], pri = trend.prior[i];
                        return [cur ? niceDate(cur.date) : '', pri ? `LY: ${niceDate(pri.date)}` : ''].filter(Boolean).join('  ·  ');
                      },
                      label: ctx => ` ${ctx.dataset.label}: ${money(ctx.parsed.y)}`,
                    },
                  },
                },
                scales: {
                  y: { ticks: { callback: moneyTick }, beginAtZero: true },
                  x: { grid: { display: false }, ticks: { maxTicksLimit: 12 } },
                },
              }}
            />
          )}
        </div>
        <div className="legend-note">
          {align === 'weekday'
            ? 'Last year = 364 days back, so weekdays line up (Fri compares to Fri).'
            : 'Last year = same calendar dates; weekday mix may differ.'}
        </div>
      </Card>

      <Card tour="sss-table" title="Comps by Location" sub="new or closed locations show “—” and are excluded from the fleet comp">
        <DataTable
          csvName="same-store-sales.csv"
          defaultSort={{ key: 'comp', dir: 'desc' }}
          columns={[
            {
              key: 'locationName', label: 'Location',
              render: r => <>{r.locationName}{!r.isComp && <span className="pill" style={{ marginLeft: 8 }}>{r.prevNetSales ? 'closed' : 'new'}</span>}</>,
              csv: r => r.locationName,
            },
            { key: 'currNetSales', label: 'Net Sales', num: true, render: r => money(r.currNetSales), csv: r => (r.currNetSales / 100).toFixed(2), footer: rows => money(sum(rows.filter(x => x.isComp), 'currNetSales')) },
            { key: 'prevNetSales', label: 'LY Net Sales', num: true, render: r => r.prevNetSales != null ? money(r.prevNetSales) : '—', csv: r => r.prevNetSales != null ? (r.prevNetSales / 100).toFixed(2) : '', footer: rows => money(sum(rows.filter(x => x.isComp), 'prevNetSales')) },
            {
              key: 'comp', label: 'Comp %', num: true,
              sortValue: r => (r.isComp ? (r.currNetSales - r.prevNetSales) / r.prevNetSales : -Infinity),
              render: r => r.isComp ? <Delta curr={r.currNetSales} prev={r.prevNetSales} /> : '—',
              csv: r => r.isComp ? (((r.currNetSales - r.prevNetSales) / r.prevNetSales) * 100).toFixed(1) : '',
              footer: rows => {
                const c = rows.filter(x => x.isComp);
                const cn = sum(c, 'currNetSales'), pn = sum(c, 'prevNetSales');
                return pn > 0 ? <Delta curr={cn} prev={pn} /> : '—';
              },
            },
            { key: 'currOrders', label: 'Orders', num: true, render: r => int(r.currOrders), csv: r => r.currOrders },
            { key: 'prevOrders', label: 'LY Orders', num: true, render: r => r.prevOrders != null ? int(r.prevOrders) : '—', csv: r => r.prevOrders ?? '' },
            {
              key: 'ordersComp', label: 'Orders Comp %', num: true,
              sortValue: r => (r.isComp && r.prevOrders ? (r.currOrders - r.prevOrders) / r.prevOrders : -Infinity),
              render: r => r.isComp && r.prevOrders ? <Delta curr={r.currOrders} prev={r.prevOrders} /> : '—',
              csv: r => r.isComp && r.prevOrders ? (((r.currOrders - r.prevOrders) / r.prevOrders) * 100).toFixed(1) : '',
            },
          ]}
          rows={locations.map(r => ({ ...r, __key: r.locationId }))}
          footer
        />
      </Card>
    </div>
  );
}
